"use client";

import { useCallback, useEffect, useState } from "react";
import { computeCleanupSuggestions, type ScanProgress } from "@/lib/emailCleaner/compute";
import { formatBytes, formatCount } from "@/lib/emailCleaner/format";
import { listSample, performMessageAction } from "@/lib/emailCleaner/gmail";
import { cleanupHistoryStore } from "@/lib/emailCleaner/stores";
import { useBulkActionWithProgress, type BulkProgress } from "@/lib/emailCleaner/useBulkActionWithProgress";
import { useViewStack } from "@/lib/emailCleaner/useViewStack";
import type { NormalizedEmail, RealCleanupSuggestion } from "@/lib/emailCleaner/types";
import { BulkActionProgressOverlay, Card, EmptyState, PrimaryButton, ScanProgressBar, ScreenHeader } from "../Shared";

type View = { name: "root" } | { name: "preview"; suggestion: RealCleanupSuggestion } | { name: "history" };

type AccountProps = { accessToken: string; accountEmail: string };
type Props = AccountProps & { onExit: () => void };

export default function SmartCleanTab({ accessToken, accountEmail, onExit }: Props) {
  const { view, depth, push, pop } = useViewStack<View>({ name: "root" });
  const back = depth > 1 ? pop : onExit;

  if (view.name === "preview") {
    return <PreviewScreen accessToken={accessToken} accountEmail={accountEmail} suggestion={view.suggestion} onBack={back} />;
  }
  if (view.name === "history") {
    return <HistoryScreen onBack={back} />;
  }
  return (
    <RootScreen
      accessToken={accessToken}
      accountEmail={accountEmail}
      onBack={back}
      onOpenPreview={(s) => push({ name: "preview", suggestion: s })}
      onOpenHistory={() => push({ name: "history" })}
    />
  );
}

function RootScreen({
  accessToken,
  accountEmail,
  onBack,
  onOpenPreview,
  onOpenHistory,
}: AccountProps & { onBack: () => void; onOpenPreview: (s: RealCleanupSuggestion) => void; onOpenHistory: () => void }) {
  const [suggestions, setSuggestions] = useState<RealCleanupSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ScanProgress | null>(null);

  const load = useCallback(() => {
    setSuggestions(null);
    setError(null);
    computeCleanupSuggestions(accessToken, setProgress)
      .then(setSuggestions)
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load suggestions"));
  }, [accessToken]);

  useEffect(() => {
    // Loading real Gmail-backed suggestions on mount is the intended sync effect here.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
    // accountEmail intentionally not used here — computeCleanupSuggestions is account-scoped via accessToken.
  }, [load, accountEmail]);

  const totalCount = suggestions?.reduce((a, s) => a + s.estimatedCount, 0) ?? 0;
  const totalBytes = suggestions?.reduce((a, s) => a + s.estimatedBytes, 0) ?? 0;

  return (
    <div>
      <ScreenHeader
        title="Smart Clean"
        onBack={onBack}
        action={
          <button type="button" onClick={onOpenHistory} className="text-sm font-medium text-violet-600 dark:text-violet-400">
            History
          </button>
        }
      />

      {error && <Card className="text-sm text-red-600 dark:text-red-400">{error}</Card>}
      {!error && !suggestions && progress && <ScanProgressBar {...progress} />}

      {suggestions && (
        <Card className="mb-4 flex items-center gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-violet-50 text-sm font-bold text-violet-700 dark:bg-violet-500/10 dark:text-violet-400">
            {formatBytes(totalBytes)}
          </div>
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {totalCount} emails can safely be cleaned, freeing an estimated {formatBytes(totalBytes)}.
          </p>
        </Card>
      )}

      {suggestions && suggestions.length === 0 && <EmptyState>Nothing to clean up right now 🎉</EmptyState>}
      {suggestions?.map((s) => (
        <Card key={s.category} onClick={() => onOpenPreview(s)} className="mb-3">
          <p className="font-medium text-slate-900 dark:text-white">{s.category}</p>
          <p className="text-sm text-slate-500 dark:text-slate-400">{s.reason}</p>
          <p className="mt-1 text-sm font-medium text-violet-600 dark:text-violet-400">
            {s.estimatedCount} emails • {formatBytes(s.estimatedBytes)}
          </p>
        </Card>
      ))}
    </div>
  );
}

function PreviewScreen({
  accessToken,
  accountEmail,
  suggestion,
  onBack,
}: AccountProps & { suggestion: RealCleanupSuggestion; onBack: () => void }) {
  const [sample, setSample] = useState<NormalizedEmail[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [selectedProgress, setSelectedProgress] = useState<BulkProgress | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const { run, progress: bulkProgress } = useBulkActionWithProgress(accessToken);

  useEffect(() => {
    listSample(accessToken, accountEmail, suggestion.query, 50).then((emails) => {
      setSample(emails);
      setSelected(new Set(emails.map((e) => e.id)));
    });
  }, [accessToken, accountEmail, suggestion.query]);

  async function cleanSelected() {
    if (!sample) return;
    if (!window.confirm(`Delete ${selected.size} selected emails from "${suggestion.category}"? This cannot be undone.`)) return;
    setBusy(true);
    const targets = sample.filter((e) => selected.has(e.id));
    for (let i = 0; i < targets.length; i += 1) {
      await performMessageAction(accessToken, targets[i].id, "trash");
      setSelectedProgress({ processed: i + 1, total: targets.length });
    }
    cleanupHistoryStore.add({
      date: new Date().toISOString(),
      category: suggestion.category,
      emailsCleaned: targets.length,
      bytesFreed: targets.reduce((a, e) => a + e.size_bytes, 0),
    });
    setBusy(false);
    setSelectedProgress(null);
    setResult(`Cleaned ${targets.length} emails`);
    setSample(sample.filter((e) => !selected.has(e.id)));
    setSelected(new Set());
  }

  async function cleanAllReal() {
    setBusy(true);
    const count = await run({ query: suggestion.query, actionType: "trash", historyLabel: suggestion.category, estimatedCount: suggestion.estimatedCount });
    setBusy(false);
    if (count === null) return;
    setResult(`Cleaned ${count} emails`);
    setSample([]);
    setSelected(new Set());
  }

  return (
    <div>
      <BulkActionProgressOverlay progress={selectedProgress ?? bulkProgress} />
      <ScreenHeader title={suggestion.category} onBack={onBack} />
      <Card className="mb-4 text-sm text-slate-600 dark:text-slate-400">{suggestion.reason}</Card>
      {result && <Card className="mb-4 text-sm text-emerald-700 dark:text-emerald-400">{result}</Card>}

      {!sample && <ScanProgressBar stage="Loading preview…" current={0} total={1} />}
      {sample && sample.length === 0 && !result && <EmptyState>Nothing to preview.</EmptyState>}

      {sample && sample.length > 0 && (
        <>
          <div className="mb-3 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>{selected.size} of {sample.length} selected (preview of {formatCount(suggestion.estimatedCount)} total)</span>
            <button
              type="button"
              onClick={() => setSelected(selected.size === sample.length ? new Set() : new Set(sample.map((e) => e.id)))}
              className="font-medium text-violet-600 dark:text-violet-400"
            >
              {selected.size === sample.length ? "Deselect all" : "Select all"}
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {sample.map((e) => {
              const checked = selected.has(e.id);
              return (
                <Card key={e.id} className="mb-2">
                  <label className="flex cursor-pointer items-center gap-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (checked) next.delete(e.id);
                          else next.add(e.id);
                          return next;
                        })
                      }
                      className="h-4 w-4 accent-red-600"
                    />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{e.from}</p>
                      <p className="truncate text-xs text-slate-500 dark:text-slate-400">{e.subject}</p>
                    </div>
                  </label>
                </Card>
              );
            })}
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <PrimaryButton onClick={cleanSelected} disabled={busy || selected.size === 0} danger>
              {busy ? "Cleaning…" : `Trash ${selected.size} selected`}
            </PrimaryButton>
            {suggestion.estimatedCount > sample.length && (
              <button
                type="button"
                onClick={cleanAllReal}
                disabled={busy}
                className="rounded-full border border-red-300 py-2 text-sm font-semibold text-red-600 disabled:opacity-60 dark:border-red-700 dark:text-red-400"
              >
                {busy ? "Working…" : `Trash all ${formatCount(suggestion.estimatedCount)} matching emails`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function HistoryScreen({ onBack }: { onBack: () => void }) {
  const [entries] = useState(() => cleanupHistoryStore.load());
  return (
    <div>
      <ScreenHeader title="Cleanup History" onBack={onBack} />
      {entries.length === 0 && <EmptyState>No cleanups yet.</EmptyState>}
      {entries.map((entry, i) => (
        <Card key={i} className="mb-2 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-900 dark:text-white">{entry.category}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {entry.emailsCleaned} emails • {formatBytes(entry.bytesFreed)} freed
            </p>
          </div>
          <span className="text-xs text-slate-400">{new Date(entry.date).toLocaleDateString()}</span>
        </Card>
      ))}
    </div>
  );
}
